import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  uploadPhoto,
  deletePhoto,
  updatePhotoCaption,
  getSessionPhotos,
  photoFileUrl,
} from "../services/uploadService";

export const MAX_PHOTOS = 10;
const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXT = /\.(jpe?g|png|heic|heif)$/i;
const HEIC_EXT = /\.(heic|heif)$/i;

const newLocalId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getOrCreateSessionId = () => {
  let id = sessionStorage.getItem("photoUploadSessionId");
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("photoUploadSessionId", id);
  }
  return id;
};

export const usePhotoUpload = () => {
  const [photos, setPhotos] = useState([]);
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) sessionIdRef.current = getOrCreateSessionId();
  const sessionId = sessionIdRef.current;

  const updateEntry = useCallback((localId, patch) => {
    setPhotos((prev) => prev.map((p) => (p.localId === localId ? { ...p, ...patch } : p)));
  }, []);

  // Restore already-uploaded photos for this session (refresh / back navigation)
  useEffect(() => {
    getSessionPhotos(sessionId)
      .then((serverPhotos) => {
        if (!serverPhotos?.length) return;
        setPhotos((prev) => {
          const known = new Set(prev.map((p) => p.photo_id).filter(Boolean));
          const restored = serverPhotos
            .filter((sp) => !known.has(sp.photo_id))
            .map((sp) => ({
              localId: newLocalId(),
              photo_id: sp.photo_id,
              file: null,
              name: sp.original_filename,
              size: sp.size,
              caption: sp.caption || "",
              status: "done",
              progress: 100,
              previewUrl: photoFileUrl(sp.photo_id, { sessionId }),
              error: null,
            }));
          return [...prev, ...restored];
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startUpload = useCallback(
    (entry) => {
      uploadPhoto(entry.file, sessionId, (progress) => updateEntry(entry.localId, { progress }))
        .then((data) => {
          updateEntry(entry.localId, {
            status: "done",
            progress: 100,
            photo_id: data.photo_id,
            ...(HEIC_EXT.test(entry.name || "")
              ? { previewUrl: photoFileUrl(data.photo_id, { sessionId }) }
              : {}),
          });
          const current = photosRef.current.find((p) => p.localId === entry.localId);
          if (current?.caption) {
            updatePhotoCaption(data.photo_id, current.caption, sessionId).catch(() => {});
          }
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail || "Upload failed. Please try again.";
          updateEntry(entry.localId, { status: "error", error: detail });
        });
    },
    [sessionId, updateEntry]
  );

  const addFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      const activeCount = photosRef.current.filter((p) => p.status !== "error").length;
      const slots = MAX_PHOTOS - activeCount;
      if (slots <= 0) {
        toast.error(`You can upload up to ${MAX_PHOTOS} photos per request`);
        return;
      }
      if (files.length > slots) {
        toast.error(`Only ${slots} more photo${slots !== 1 ? "s" : ""} can be added (max ${MAX_PHOTOS})`);
      }
      const entries = [];
      for (const file of files.slice(0, slots)) {
        if (!ALLOWED_EXT.test(file.name || "")) {
          toast.error(`${file.name}: unsupported format. Please use JPG, PNG, or HEIC.`);
          continue;
        }
        if (file.size > MAX_SIZE_BYTES) {
          toast.error(`${file.name}: file is larger than 10 MB`);
          continue;
        }
        entries.push({
          localId: newLocalId(),
          photo_id: null,
          file,
          name: file.name,
          size: file.size,
          caption: "",
          status: "uploading",
          progress: 0,
          previewUrl: HEIC_EXT.test(file.name) ? null : URL.createObjectURL(file),
          error: null,
        });
      }
      if (!entries.length) return;
      setPhotos((prev) => [...prev, ...entries]);
      entries.forEach(startUpload);
    },
    [startUpload]
  );

  const removePhoto = useCallback(
    (localId) => {
      const entry = photosRef.current.find((p) => p.localId === localId);
      if (!entry) return;
      if (entry.previewUrl && entry.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(entry.previewUrl);
      }
      if (entry.photo_id) deletePhoto(entry.photo_id, sessionId).catch(() => {});
      setPhotos((prev) => prev.filter((p) => p.localId !== localId));
    },
    [sessionId]
  );

  const setCaption = useCallback(
    (localId, caption) => updateEntry(localId, { caption }),
    [updateEntry]
  );

  const saveCaption = useCallback(
    (localId) => {
      const entry = photosRef.current.find((p) => p.localId === localId);
      if (entry?.photo_id) {
        updatePhotoCaption(entry.photo_id, entry.caption || "", sessionId).catch(() => {});
      }
    },
    [sessionId]
  );

  const retryUpload = useCallback(
    (localId) => {
      const entry = photosRef.current.find((p) => p.localId === localId);
      if (!entry || !entry.file) return;
      updateEntry(localId, { status: "uploading", progress: 0, error: null });
      startUpload(entry);
    },
    [startUpload, updateEntry]
  );

  const isUploading = photos.some((p) => p.status === "uploading");
  const uploadedPhotos = photos.filter((p) => p.status === "done");

  return {
    photos,
    sessionId,
    addFiles,
    removePhoto,
    setCaption,
    saveCaption,
    retryUpload,
    isUploading,
    uploadedPhotos,
  };
};
